// angular
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// addons
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, mergeMap, catchError, tap, withLatestFrom, filter } from 'rxjs/operators';
import { of } from 'rxjs';

// service
import { TradeService, OrganizationService } from '@core/services';

// store
import { State } from '@store/index';
import { Store, select } from '@ngrx/store';
import * as actions from './actions';
import * as tradeActions from '../trade/actions';
import * as userSelectors from '../user/selectors';
import * as organizationSelectors from '../organization/selectors';

// model
import { ITradeSlim, TradeStatus, UserRolesEnum, IOrganization } from '@shared/models';

@Injectable()
export class TradesEffects {
  loadTrades$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Load),
    mergeMap(({ status }: { status: TradeStatus }) => {
      return this.tradeService.getTrades({ status }).pipe(
        map((trades: ITradeSlim[]) => actions.loadSuccess({ trades })),
        catchError(error => of(actions.loadFail({ error })))
      );
    })
  ));

  loadTradeAfterTrades$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.LoadSuccess),
    withLatestFrom(this.store.pipe(select(userSelectors.getRole))),
    filter(([_, role]: [{ trades: ITradeSlim[] }, string]) => role !== UserRolesEnum.Notary),
    filter(([_, role]: [{ trades: ITradeSlim[] }, string]) => !this.route.snapshot.fragment),
    map(([{ trades }, _]: [{ trades: ITradeSlim[] }, string]) => {
      if (trades.length > 0) {
        return tradeActions.load({ id: trades[0].id });
      } else {
        return tradeActions.clear();
      }
    })
  ));

  navigateAfterTradeAdd$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.Add),
    tap(({ trade }: { trade: ITradeSlim }) => this.router.navigate([], {
      queryParams: {
        status: trade.status
      },
      fragment: String(trade.id)
    }))
  ), { dispatch: false });

  loadTradesPerOrganization$ = createEffect(() => this.actions$.pipe(
    ofType(actions.ActionTypes.LoadPerOrganization),
    withLatestFrom(this.store.pipe(select(organizationSelectors.getOrganization))),
    mergeMap(([{ status }, organization]: [{ status: TradeStatus }, IOrganization]) => {
      return this.organizationService.getTrades(organization.id).pipe(
        map((trades: ITradeSlim[]) => actions.loadSuccess({ trades })),
        catchError(error => of(actions.loadFail({ error })))
      );
    })
  ));

  constructor(
    private actions$: Actions,
    private tradeService: TradeService,
    private organizationService: OrganizationService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<State>
  ) {}
}
